/**
 * Open-water colour and how see-through it is, driven by a 0–1 light amount (sky brightness or
 * local irradiance after occlusion). Kept next to `daylight()` so play, the editor, and tests
 * agree on what noon water looks like.
 */

import { clamp } from '../math.ts';

export type WaterAppearance = {
  /** Opaque bed under the water sheet; reefs sit on this and show through the sheet. */
  seafloor: number;
  /** Translucent sheet tint. */
  color: number;
  /** Sheet opacity: lower in strong light so underwater objects read through. */
  alpha: number;
};

/** Current dark navy, kept as the night end of the lerp. */
export const NIGHT_WATER_COLOR = 0x0b1d2a;
/** Much lighter tropical blue at noon. */
export const DAY_WATER_COLOR = 0x8ad8f2;
/** Visible teal bed under the sheet so noon water stays light while reefs still show through. */
export const SEAFLOOR_COLOR = 0x2f6f80;

export const NIGHT_WATER_ALPHA = 0.88;
export const DAY_WATER_ALPHA = 0.48;

/** Pale aqua the sun lifts a shallow bed toward, so it reads as thinner water, not a sticker. */
export const SHALLOW_SUN_LIFT = 0xdff6f2;
/** How far a night bed sinks toward the seafloor. */
export const BED_NIGHT_SINK = 0.42;
/** How far a noon bed lifts toward `SHALLOW_SUN_LIFT`. */
export const BED_SUN_MIX = 0.32;

export function waterAppearance(light: number): WaterAppearance {
  const t = clamp(light, 0, 1);

  return {
    seafloor: SEAFLOOR_COLOR,
    color: mixRgb(NIGHT_WATER_COLOR, DAY_WATER_COLOR, t),
    alpha: NIGHT_WATER_ALPHA + (DAY_WATER_ALPHA - NIGHT_WATER_ALPHA) * t,
  };
}

/**
 * Bed colour under the water sheet. Real shallows look lighter because a short
 * column lets the bright bed show through; at night that bed sinks toward the
 * seafloor so it does not glow.
 */
export function sunlitBed(fill: number, skyLight: number, seafloor: number): number {
  const t = clamp(skyLight, 0, 1);
  const night = mixRgb(fill, seafloor, BED_NIGHT_SINK);
  const day = mixRgb(fill, SHALLOW_SUN_LIFT, BED_SUN_MIX);

  return mixRgb(night, day, t);
}

function mixRgb(from: number, to: number, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  const fr = (from >> 16) & 0xff;
  const fg = (from >> 8) & 0xff;
  const fb = from & 0xff;
  const tr = (to >> 16) & 0xff;
  const tg = (to >> 8) & 0xff;
  const tb = to & 0xff;
  const r = Math.round(fr + (tr - fr) * t);
  const g = Math.round(fg + (tg - fg) * t);
  const b = Math.round(fb + (tb - fb) * t);
  return (r << 16) | (g << 8) | b;
}
